/**
 * MLS-MAILAWAY-ENTITY-001 — MLS Mail-Away rows for Order Summary → Mail-Away Requests, rendered
 * Tax-style so Tax and MLS requests coexist in ONE section. MLS-scoped endpoints only (receive /
 * resend / cancel under /components/mls/mail-away) — never the Tax engine.
 * standalone: renders its own header + Total MLC Fees (no Tax mail-away on the order).
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, MapPin, CheckCircle, RotateCcw, XCircle, Paperclip, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { MailAwayStatusBadge } from '../mailaway/shared/MailAwayStatusBadge';
import { MailAwayResponseModal } from '../mailaway/shared/MailAwayResponseModal';
import { MailAwayCancelModal } from '../mailaway/shared/MailAwayCancelModal';
import { MailAwayAttachmentViewer } from '../mailaway/shared/MailAwayAttachmentViewer';
import { MAIL_AWAY_ACTIVE } from '../mailaway/shared/mailAwayStatus';
import { MlsMailAwaySendModal, uploadMlsFiles, fmtFee, feeOf } from './MlsMailAwaySendModal';
import { buildChains } from './MlsMailAwayModal';

const API = process.env.REACT_APP_BACKEND_URL;
const FEE_EXCLUDED = new Set(['cancelled', 'superseded', 'failed']);

export const isMlsMailAwayRow = (r) => (r?.component || 'tax') === 'mls';
/** Latest cycle of each chain, cancelled excluded — mirrors the Tax Total MLC Fees rule. */
export const mlsMailAwaySummary = (rows) => {
  const heads = buildChains(rows).map((c) => c[0]);
  const fee = heads.filter((r) => !FEE_EXCLUDED.has(String(r.status || '').toLowerCase())).reduce((s, r) => s + (feeOf(r.mlc_fee) || 0), 0);
  return {
    total: heads.length,
    pending: heads.filter((r) => MAIL_AWAY_ACTIVE.includes(r.status)).length,
    received: heads.filter((r) => r.status === 'response_received').length,
    cancelled: heads.filter((r) => r.status === 'cancelled').length,
    fee, feeCount: heads.filter((r) => !FEE_EXCLUDED.has(String(r.status || '').toLowerCase()) && feeOf(r.mlc_fee) > 0).length,
  };
};

const MlsRow = ({ chain, canAct, onReceive, onResend, onCancel, onOpenAttachment }) => {
  const req = chain[0];
  const active = MAIL_AWAY_ACTIVE.includes(req.status);
  const rid = req.request_id;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-slate-200 bg-white flex-wrap" data-testid={`mls-summary-ma-row-${rid}`} data-status={req.status}>
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <MapPin size={14} className="text-slate-400 shrink-0" />
        <span className="text-sm font-semibold text-slate-800 uppercase truncate" data-testid={`mls-summary-ma-name-${rid}`}>{req.department_name}</span>
        <MailAwayStatusBadge request={req} testId={`mls-summary-ma-status-${rid}`} />
        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide bg-purple-100 text-purple-800 border border-purple-200" data-testid={`mls-summary-ma-mls-badge-${rid}`}>MLS</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`mls-summary-ma-section-${rid}`}>{req.section_label || req.section}</span>
        <span className="text-[10px] text-slate-500 uppercase">{req.method}</span>
        {(req.cycle_index || 1) > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase bg-violet-100 text-violet-800 border border-violet-200">Cycle {req.cycle_index}</span>}
        {fmtFee(req.mlc_fee)
          ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`mls-summary-ma-fee-${rid}`}>{fmtFee(req.mlc_fee)}</span>
          : <span className="text-[10px] text-slate-400" data-testid={`mls-summary-ma-fee-${rid}`}>fee —</span>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {req.attachments?.length > 0 && (
          <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px]" data-testid={`mls-summary-ma-attachments-${rid}`} onClick={() => onOpenAttachment(req.attachments[0])}>
            <Paperclip className="w-3 h-3 mr-1" /> Attachment{req.attachments.length > 1 ? `s (${req.attachments.length})` : ''}
          </Button>
        )}
        {canAct && active && (
          <>
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px] text-blue-700 border-blue-300 hover:bg-blue-50" data-testid={`mls-summary-ma-resend-${rid}`} onClick={() => onResend(req)}>
              <RotateCcw className="w-3 h-3 mr-1" /> Resend
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px] text-green-700 border-green-300 hover:bg-green-50" data-testid={`mls-summary-ma-receive-${rid}`} onClick={() => onReceive(req)}>
              <CheckCircle className="w-3 h-3 mr-1" /> Received
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px] text-red-600 border-red-300 hover:bg-red-50" data-testid={`mls-summary-ma-cancel-${rid}`} onClick={() => onCancel(req)}>
              <XCircle className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export const MlsMailAwaySummaryRows = ({ orderId, requests = [], canAct = true, standalone = false, onChanged }) => {
  const [receiveReq, setReceiveReq] = useState(null);
  const [cancelReq, setCancelReq] = useState(null);
  const [resendReq, setResendReq] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [busy, setBusy] = useState(false);
  const chains = useMemo(() => buildChains(requests), [requests]);
  const sum = useMemo(() => mlsMailAwaySummary(requests), [requests]);
  if (!requests.length) return null;

  const doReceive = async (notes, files) => {
    setBusy(true);
    try {
      const attachments = files?.length ? await uploadMlsFiles(orderId, files, { section: receiveReq.section, utility_type: receiveReq.utility_type, mail_away_request_id: receiveReq.request_id }) : [];
      await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/${receiveReq.request_id}/receive`, { response_note: notes || null, attachments });
      toast.success(`Response received — ${receiveReq.department_name}`);
      setReceiveReq(null); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record response'); }
    setBusy(false);
  };
  const doCancel = async (reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/${cancelReq.request_id}/cancel`, { reason });
      toast.success(`Mail-Away cancelled — ${cancelReq.department_name}`);
      setCancelReq(null); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
    setBusy(false);
  };

  return (
    <div className="space-y-2" data-testid="mls-summary-mailaway">
      {standalone && (
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-blue-100 rounded-lg"><Mail size={20} className="text-blue-600" /></div>
          <div>
            <h3 className="font-semibold text-gray-900">Mail-Away Requests</h3>
            <p className="text-sm text-gray-500" data-testid="mls-summary-ma-counts">
              {sum.total} {sum.total === 1 ? 'request' : 'requests'} • {sum.pending} pending • {sum.received} received{sum.cancelled > 0 ? ` • ${sum.cancelled} cancelled` : ''}
            </p>
          </div>
        </div>
      )}
      {chains.map((chain) => (
        <MlsRow key={chain[0].request_id} chain={chain} canAct={canAct} onReceive={setReceiveReq} onResend={setResendReq} onCancel={setCancelReq} onOpenAttachment={setAttachment} />
      ))}
      {standalone && sum.feeCount > 0 && (
        <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="mailaway-total-mlc-fees">
          <div className="flex items-center gap-2">
            <DollarSign size={16} className="text-emerald-600" />
            <span className="text-sm font-medium text-emerald-800">Total MLC Fees</span>
            <span className="text-xs text-emerald-600">({sum.feeCount} {sum.feeCount === 1 ? 'entity' : 'entities'} · MLS)</span>
          </div>
          <span className="text-sm font-bold text-emerald-800" data-testid="mailaway-total-mlc-fees-amount">${sum.fee.toFixed(2)}</span>
        </div>
      )}
      {receiveReq && <MailAwayResponseModal open targetName={receiveReq.department_name} loading={busy} onConfirm={doReceive} onClose={() => setReceiveReq(null)} testIdPrefix="mls-summary-ma" />}
      {cancelReq && <MailAwayCancelModal open targetName={cancelReq.department_name} loading={busy} onConfirm={doCancel} onClose={() => setCancelReq(null)} testIdPrefix="mls-summary-ma" />}
      {resendReq && (
        <MlsMailAwaySendModal key={resendReq.request_id} open orderId={orderId} mode="resend" previous={resendReq} existingRequests={requests}
          onDone={() => { setResendReq(null); onChanged?.(); }} onClose={() => setResendReq(null)} />
      )}
      {attachment && <MailAwayAttachmentViewer open orderId={orderId} attachment={attachment} onOpenChange={(v) => { if (!v) setAttachment(null); }} />}
    </div>
  );
};

export default MlsMailAwaySummaryRows;
